import { Component, inject, signal } from '@angular/core';
import { ValidationErrorComponent } from '../../../components/core/validation-error/validation-error.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { ResetPasswordRequest, ResetPasswordSuccess, AuthValidationError, SetPasswordRequest, SetPasswordSuccess } from '../../../store/auth/auth.actions';
import { BRANDING } from '../../../core/config/branding';

@Component({
  standalone: true,
  selector: 'app-set-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    RouterLink,
    ValidationErrorComponent
  ],
  templateUrl: './set-password.html',
  styleUrl: './set-password.scss',
})
export class SetPassword {
  protected readonly brand = BRANDING;

  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  validationError = signal<string[]>([]);
  constructor() {
    const token = this.route.snapshot.paramMap.get('token') ?? this.route.snapshot.queryParamMap.get('token');
    if (token) {
      this.setPasswordForm.patchValue({ token });
    }
  }

  protected readonly setPasswordForm = this.formBuilder.group(
    {
      token: [''],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
    },
    { validators: this.matchPasswords }
  );

  protected onSubmit(): void {
    if (this.setPasswordForm.invalid) {
      this.setPasswordForm.markAllAsTouched();
      return;
    }
    this.store.dispatch(SetPasswordRequest({ payload: { token: String(this.setPasswordForm.value.token), password: String(this.setPasswordForm.value.password) } }));
    
    this.actions$.pipe(ofType(SetPasswordSuccess), take(1)).subscribe(() => {
      this.router.navigate(['/auth/login']);
    });

    this.actions$.pipe(ofType(AuthValidationError), take(1)).subscribe(({ validationError }) => {
      this.validationError.set(validationError);
    });
  }

  private matchPasswords(control: AbstractControl): { passwordMismatch: true } | null {
    const passwordControl = control.get('password');
    const confirmControl = control.get('confirmPassword');
    const password = passwordControl?.value;
    const confirmPassword = confirmControl?.value;

    if (!password || !confirmPassword) {
      if (confirmControl?.hasError('passwordMismatch')) {
        const { passwordMismatch, ...rest } = confirmControl.errors ?? {};
        confirmControl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    }

    if (password === confirmPassword) {
      if (confirmControl?.hasError('passwordMismatch')) {
        const { passwordMismatch, ...rest } = confirmControl.errors ?? {};
        confirmControl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    }

    confirmControl?.setErrors({ ...(confirmControl.errors ?? {}), passwordMismatch: true });
    return { passwordMismatch: true };
  }

}
